import { releaseNotesRoute } from '@/components/ReleaseNotes'
import { accountManagementRoute } from '@/features/AccountManagement/pages/AccountManagement'
import { auditLogsRoute } from '@/features/AuditLogs/pages/AuditLogsPage/AuditLogsPage'
import { newClusterConfigRoute } from '@/features/ClusterConfigs/components/NewClusterConfig/NewClusterConfig'
import { configRevisionComparison } from '@/features/ClusterConfigs/components/States/ConfigRevisionComparison'
import { clusterConfigDiffRoute } from '@/features/ClusterConfigs/pages/ClusterConfigDiff/ClusterRevisionDetails'
import { clusterConfigDraftRoute } from '@/features/ClusterConfigs/pages/ClusterConfigDraft/ClusterConfigDraft'
import { clusterConfigRevisionsRoute } from '@/features/ClusterConfigs/pages/ClusterConfigRevisions/ClusterConfigRevisions'
import { clusterConfigTableRoute } from '@/features/ClusterConfigs/pages/ClusterConfigTable/ClusterConfigTable'
import { clusterConfigsRootRoute } from '@/features/ClusterConfigs/pages/ClusterConfigsPage/ClusterConfigsPage'
import { clusterUpdateTableRoute } from '@/features/ClusterConfigs/pages/ClusterUpdateTable/ClusterUpdateTable'
import { clusterUpdateDetailsRoute } from '@/features/ClusterConfigs/pages/ClusterUpdateTable/ClusterUpdateTaskDetails'
import { newClusterRoute } from '@/features/ClusterConfigs/pages/NewCluster/NewCluster'
import { hivesOverviewClusterDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewClusterDetails'
import { hivesOverviewRoute } from '@/features/HivesOverview/pages/HivesOverviewHivesTable'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import { licenseDetailRoute } from '@/features/Licenses/pages/LicenseDetailPage'
import { salesforceAccountDetailRoute } from '@/features/SalesforceAccounts/pages/SalesforceAccountDetailPage'
import { salesforceAccountsIndexRoute } from '@/features/SalesforceAccounts/pages/SalesforceAccountsIndexPage'
import { salesforceIngestResolveRoute } from '@/features/SalesforceIngest/pages/ResolveIngestPage'
import { dynamicTemplateComparison } from '@/features/Templates/components/TemplateComparison'
import { templateCreateRoute } from '@/features/Templates/components/TemplateCreate'
import { templateDetailsRoute } from '@/features/Templates/components/TemplateDetails'
import { templateTableRoute } from '@/features/Templates/page/TemplateTable'
import { createRouter } from '@tanstack/react-router'
import { kafkaExtensionRoute } from '../features/extensions/KafkaExtension/KafkaExtensionPage'
import { loginCallbackRoute, loginRoute } from './authentication/login'
import {
  applicationRootRoute,
  auditLogsRootRoute,
  clusterUpdatesRootRoute,
  clustersRootRoute,
  hivesOverviewRootRoute,
  releaseNotesRootRoute,
  rootRoute,
  salesforceAccountsRootRoute,
  templatesRootRoute,
} from './rootRoutes'

const routeTree = rootRoute.addChildren([
  /**
   * Main application routes that define the inner contents of the application after a successful login
   */
  applicationRootRoute.addChildren([
    /**
     * All cluster routes nested withing `/clusters` as of now
     */
    clustersRootRoute.addChildren([
      clusterConfigTableRoute,
      newClusterConfigRoute,
      newClusterRoute,
      clusterConfigsRootRoute.addChildren([
        clusterConfigRevisionsRoute,
        clusterConfigDiffRoute,
        clusterConfigDraftRoute,
        configRevisionComparison,
      ]),
    ]),

    /**
     * Cluster updates route
     */
    clusterUpdatesRootRoute.addChildren([
      clusterUpdateTableRoute,
      clusterUpdateDetailsRoute,
    ]),

    /**
     * All functionality related to templates nested withing `/templates` as of now
     */
    templatesRootRoute.addChildren([
      templateTableRoute,
      templateCreateRoute,
      templateDetailsRoute,
      dynamicTemplateComparison,
    ]),

    /**
     * All functionality related to identity support nested withing `/identity` as of now
     */
    accountManagementRoute,

    /**
     * Old infra route, these are all hives not located on the unified infra
     */

    hivesOverviewRootRoute.addChildren([
      hivesOverviewRoute,
      hivesOverviewClusterDetailsRoute,
      hivesOverviewOrganizationDetailsRoute,
      licenseDetailRoute,
    ]),

    // TODO::KafkaPcC: Move this example route to the right place
    kafkaExtensionRoute,

    /**
     * Audit logs route for support investigations
     */
    auditLogsRootRoute.addChildren([auditLogsRoute]),

    /**
     * Salesforce account lookup: the reverse of the org page's link card
     */
    salesforceAccountsRootRoute.addChildren([
      salesforceAccountsIndexRoute,
      /**
       * Static, so it outranks `$salesforceAccountId` in TanStack's match
       * ordering. Registered before the dynamic sibling to keep that visible.
       */
      salesforceIngestResolveRoute,
      salesforceAccountDetailRoute,
    ]),
  ]),

  releaseNotesRootRoute.addChildren([releaseNotesRoute]),

  /**
   * !important: do not remove these routes
   * Authentication routes that are not part of the applicationRootRoute
   * and handle the whole authentication flow via okta
   */
  loginRoute,
  loginCallbackRoute,
])

export const router = createRouter({
  routeTree,
  basepath: import.meta.env.BASE_URL,
  context: {
    // biome-ignore suppressions/parse:
    // biome-ignore lint/style/noNonNullAssertion:
    auth: undefined!, // This will be set after we wrap the app in an AuthProvider
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
